import { Component, Input, ViewChild, Output, EventEmitter } from '@angular/core';
import { IFormControl } from '../../providers/formbuilder/formcontrol.interface';
import { FormControl, FormGroup, ValidatorFn, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { FormProvider } from '../../providers/myria/form-provider';
import { FormControlMapper } from '../../providers/formbuilder/formcontrol-mapper';
import { LITE_REGISTRATION_FIELDS } from '../../providers/myria/app-variables';
import { RouteProvider } from '../../providers/route/route';
import { APP_CONFIG, Region } from "../../environment";
import { RegisterProvider } from '../../providers/register/register';
import { UtilsProvider } from '../../providers/myria/utils-provider';
import { InputType } from '../../providers/formbuilder/input-type';
import { LoggerEvent, LoggerProvider } from '../../providers/logger/logger';
import { RegistrationFormDynamicField } from '../../model/types/registration-form-dynamic-type';
import { CustomerProvider } from '../../providers/customer/customer';
import { ADDITIONAL_DETAILS_FIELDS_ID } from '../../providers/myria/app-variables';

@Component({
  selector: 'rmt-dynamic-form-field',
  templateUrl: 'rmt-dynamic-form-field.html'
})
export class RmtDynamicFormFieldComponent {
  @ViewChild('input') input;
  @ViewChild('country') country;
  @Input() field: IFormControl;
  @Input() form: FormGroup;
  @Input() sanitizeFields: [string];
  @Input() removeOptional: boolean = false;
  @Input() newDesign: boolean = false;
  @Input() fields: IFormControl[];
  @Input() stepFields: IFormControl[];
  @Input() clearOnFocus: boolean = false;
  @Output() ionChange = new EventEmitter<any>();
  icon: string = 'custom-icon-info';
  defaultPhoneCountry = null;
  elementsPromise = null;
  minBirthDate;
  hidden: boolean;
  fieldsIdMap = new Map();
  customMessage: string = '';
  customMessageBE: string = '';
  isOptionalField: boolean = false;
  navInfo = this.routeProvider.pages.ListActionsheetPage
  selectableHeaderTitle;
  bankNumberValidator;
  focused: boolean = false;
  onClear;
  extraFieldSubscription;

/**
   * 8601 => Firstname
   * 8701 => Lastname
   * 18001 => email
   * 15201 => DOB
   * 15301 => Occupation
   * 4901 => Country
   * 4501 => Street
   * 4701 => State
   * 4801 => Zipcode
   * 1101 => PhoneNumber
   */
  constructor(
    public translate: TranslateService, 
    public formProvider: FormProvider, 
    private routeProvider: RouteProvider, 
    private registerProvider: RegisterProvider, 
    private utilsProvider: UtilsProvider, 
    public customerProvider: CustomerProvider
  ) {
    this.loadDynamicFieldsIdMap()
  }

  async ngOnInit() {
    if (this.field) {
      if (this.isFieldToSanitize() && this.field.value) 
      {
        this.bankNumberValidator = this.form.get(this.field.id).validator;
        this.form.get(this.field.id).clearValidators();
      }
      if (this.field.validators && this.field.label) {
        const isOptional = !this.field.validators.find(validator => validator.type === 'Required')
        const ifApplicableContent = !this.field.label.includes(this.translate.instant('form.registrationForm.optional.label_GB'));
        if(isOptional &&
           this.field.label &&
           !this.field.label.includes(this.translate.instant('page.recipientDetailsPage.optionalEmailContent')) &&
         ifApplicableContent &&
         this.field.type !== InputType.Boolean)
          this.field.label = this.field.label + ' (' + this.translate.instant('page.recipientDetailsPage.optionalEmailContent') + ')';
      }

      if (this.field.label && this.removeOptional) {
        const pos = this.field.label.lastIndexOf('(');
        if (pos >= 0)
          this.field.label = this.field.label.substr(0, pos).trim();
      }

      if (this.input && this.field.value)
        this.input.value = this.field.value;

      if(this.field.fieldModel && this.field.fieldModel.Parent && this.field.type !== InputType.Boolean) // Booleans have a different relation to their parents - they don't hide it but enable/disable it
        this.subscribeExtraField();
      else
        this.hidden = this.field.hidden

      if(this.field.dependsOn)
        this.subscribeToParentField();

      if(this.field.hasAsyncValues)
        await this.loadFieldValue(this.field.dependsOn, this.field);

      if(this.field.type === 'searchableSelect' || this.field.type === 'OptionList' ) {
        if (this.field.value)
          this.ionChange.emit(this.field);
        this.form.controls[this.field.id].valueChanges.subscribe(value => this.changed(this.field));

        if(this.field.id == RegistrationFormDynamicField.OCCUPATION){
          this.field.placeholder = this.translate.instant("global.myRia.jsonContent.occupationSearch");
          this.selectableHeaderTitle = this.field.label;
        }else if(this.field.id == "4701"){
          this.field.placeholder = this.translate.instant("global.searchStatePlaceholder");
          this.selectableHeaderTitle = this.field.label;
        } else if(this.field.id == RegistrationFormDynamicField.COUNTRY_OF_RESIDENCE ||
          this.field.id == RegistrationFormDynamicField.NATIONALITY ||
          this.field.id == RegistrationFormDynamicField.COUNTRY_OF_BIRTH){
          this.field.placeholder = this.translate.instant("CountryRegisterPage.CountrySearch");
          this.selectableHeaderTitle = this.field.label;
        } else {
          this.field.placeholder = this.field.placeholder || this.field.label;
          this.selectableHeaderTitle = this.field.label;
        }
      }

      if (this.field.errorMessage) {
        this.customMessage = this.field.errorMessage;
      }

      if(this.field.id === "8601") {
        this.customMessage = this.translate.instant('form.registrationForm.firstName.required');
      }
      if(this.field.id === "17901") {
        this.customMessage = this.translate.instant('form.registrationForm.middleName.optional');
      }
      if(this.field.id === "8701") {
        this.customMessage = this.translate.instant('form.registrationForm.lastName.required');
      }
      if(this.field.id === "46403") {
        this.customMessage = this.translate.instant('form.customerProfileForm.unitNumber.unitNumberRegex');
      }
      if(this.field.id === "46897") {
        this.customMessage = this.translate.instant('page.settingsPersonalPage.citizenshipPersonalNoRegex');
      }
      if (this.field.countryTo) {
        this.defaultPhoneCountry = this.field.countryTo;
      }
      if (this.field && this.fields) {
        const childCheckbox = this.fields.find(f => (f.fieldModel && f.fieldModel.Parent == this.field.id && f.type === InputType.Boolean));
        if (childCheckbox && this.form && this.form.controls[this.field.id]) {
          LoggerProvider.info(this, "Found child checkbox: " + childCheckbox.id);
          this.form.controls[this.field.id].setValidators(Validators.required);
          this.form.controls[this.field.id].updateValueAndValidity();
        }
      }
    }
    this.onClear = () => this.onClearValues();
  }

  private updateOptionsForIDType(options) {
     const idTypes = [];
    const objRegister: any = this.registerProvider.getCurrentRegistration();
    const liteReg = this.customerProvider.getLiteCustomerFieldsValues();

    const objNationality = liteReg && liteReg.ProvidedFields ? liteReg.ProvidedFields.find(item => item.FieldId === "16601") : objRegister.ProvidedFields.find(item => item.FieldId === "16601");

    if (options && Array.isArray(options)) {
      if (objNationality && objNationality.Value) {
        options.forEach(item => {
          if (objNationality.Value === Region.MY && item.Id === "37411")
              idTypes.push(item);
          else if (objNationality.Value !== Region.MY && item.Id !== "37411")
              idTypes.push(item);
        });
      }
    }

    return idTypes;
  }

  getCountryTitle() {
    if(this.field.id == "17201" || this.field.id == "countryOfBirth"){
      return this.translate.instant('RegistrationStep2Page.CountryOfBirth.Label');
    }  if(this.field.id == "16601"){
      if (this.field.label && this.field.label.length > 0){
        return this.field.label;
      }
      return this.translate.instant('page.countryRegisterPage.title');
    }
    return "";
  }

  private isFieldToSanitize(): boolean {
    return this.sanitizeFields && this.sanitizeFields.length > 0 && this.sanitizeFields.includes(this.field.id);
  }

  removeSpacesAndHyphens(value: string, fieldId: string) {
    const normalized = value
      .trim()
      .replace(/[\s .\-]/g, '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '');
    this.form.get(fieldId).setValue(normalized, { emitEvent: false });
  }

  onFieldBlur(ev) {
    if (this.isFieldToSanitize() && ev && ev.value) {
      this.removeSpacesAndHyphens(ev.value, this.field.id);
      if (this.bankNumberValidator) {
        this.form.get(this.field.id).setValidators(this.bankNumberValidator);
      }
      this.form.get(this.field.id).markAsTouched();
      this.form.get(this.field.id).updateValueAndValidity();
    }
  }
  
  isHideError() {
    return !this.field.validators.find(validator => validator.type === 'Required');
  }

  hasRequiredValidator(validators) {
    return validators.map(e => e.type).indexOf('Required');
  }

  ngAfterViewInit() {
    if (this.field && this.field.value && !this.field.disabled && this.field.isValid === false) {
      this.form.controls[this.field.id].markAsTouched();
      this.form.controls[this.field.id].markAsDirty();
    }
    if (this.field.id === LITE_REGISTRATION_FIELDS.COUNTRY.Id) {
      this.country.setValue(this.field.value, false);
    }
    if(this.field.id === "46897") {
      this.form.get(this.field.id).setValidators(this.getLithuaniaPersonalChecksumValidator.bind(this.form.controls[this.field.id]));
    }
  }

  changed(ev) {
    if (this.field.type === InputType.Boolean && this.fields && this.field.fieldModel && this.field.fieldModel.Parent) {
      const parent = this.fields.find(f => (f.fieldModel && f.fieldModel.FieldId == this.field.fieldModel.Parent));
      if (parent) {
        LoggerProvider.info(this, `Changed boolean ${this.field.id} for parent ${this.field.fieldModel.Parent} to ${ev.value}`);
        parent.disabled = ev.value;
        if (this.form && this.form.controls && this.form.controls[this.field.fieldModel.Parent]) {
          if (parent.disabled)
            this.form.controls[this.field.fieldModel.Parent].clearValidators();
          else
            this.form.controls[this.field.fieldModel.Parent].setValidators(Validators.required);
          this.form.controls[this.field.fieldModel.Parent].setValue('');
          this.form.controls[this.field.fieldModel.Parent].updateValueAndValidity();
          LoggerProvider.info(this, 'Updating validators after child checkbox input');
        }
      } else
        LoggerProvider.warning(this, LoggerEvent.ComponentException, `Boolean field ${this.field.id} has missing parent ${this.field.fieldModel.Parent}`)
    }
    if(this.bankNumberValidator){
      this.form.get(this.field.id).setValidators(this.bankNumberValidator);
    }
    if (ev.prefix) {
      this.field.numberPhoneValue = ev.prefix + ev.phone;
      this.field.phoneParts = {prefix: ev.prefix, phone: ev.phone}
    } else if (this.isFieldToSanitize()) {
      this.removeSpacesAndHyphens(ev.value, this.field.id);
    } else {
      ev = this.field; // added this incase of date of birth ev doesn't have id and change event in the dyncamic rmt-dyncamic-form(this.form.controls[field.id].value) is expecting it.
    }

    if (APP_CONFIG.region.region === Region.CL)
      this.updateErrorMsgForChileanNationalID(this.field);

    this.ionChange.emit(ev);
  }

 loadValue(field: any, parentValue?: any){
    return this.formProvider.getOptionsFields(field, parentValue);
  }


  async loadFieldValue(dependsOn: number, field): Promise<any> {
    this.elementsPromise = new Promise((resolve) => {

      if(!dependsOn) {
        this.loadValue(field.id).subscribe(options => {
          this.translateOptions(field.id, options);
          if (field.id === "14601" && APP_CONFIG.region.region === Region.MY) {
            options = this.updateOptionsForIDType(options);
          }

          if (field.fieldModel)
            field.fieldModel.Options = options;
          field.options = FormControlMapper.mapFieldOptions(options);
          if(field.value) {
            let value = field.options.find(option => { return option.value == field.value});
            if(value) this.form.controls[field.id].setValue(value.value);
            else {
              this.form.controls[field.id].setValue(null);
              this.form.controls[field.id].enable();
            }
          }
          resolve(options);
        }, () => {
          resolve(null);
        });
        return;
      }
      else if(this.form.controls[dependsOn]) {
        let parentValue = this.form.controls[dependsOn].value;
          if(parentValue != undefined && parentValue != "") {
            this.loadValue(field.id, parentValue).subscribe(options => {
              this.translateOptions(field.id, options);
              if (field.id === "14601" && APP_CONFIG.region.region === Region.MY) {
                options = this.updateOptionsForIDType(options);
              }

              if (field.fieldModel)
                field.fieldModel.Options = options;
              field.options = FormControlMapper.mapFieldOptions(options);
              // if(this.preFilledValue) {
              //   let value = field.Options.find(option => { return option.Id == this.preFilledValue});
              //   this.form.controls[field.FieldId].setValue(value.Id);
              // }
              resolve(options);
            }, () => {
              resolve(null);
            });
          return;
          }
      }
      resolve(null)
    });
  }
  translateOptions(id: any, options: any) {
    options.forEach(option => FormControlMapper.getTranslatedOption(id, option));
  }

  subscribeToParentField() {
    this.form.controls[this.field.dependsOn].valueChanges.subscribe(value => {
      this.form.controls[this.field.id].reset();
      if(value !== "") {
        this.elementsPromise = new Promise((resolve) => {
        this.loadValue(this.field.id, value).subscribe(options => {
          this.translateOptions(this.field.id, options);
          if (this.field.fieldModel)
          this.field.fieldModel.Options = options;
          this.field.options = options;
          resolve(options);
        }, () => {
          resolve(null);
        });
        });
      }
    });
  }
  getDynamicId(fieldId,fieldType){
    if(!fieldId || fieldId==""){
      return '';
    }
    fieldId = this.fieldsIdMap.has(fieldId)?this.fieldsIdMap.get(fieldId):fieldId
    switch (fieldType) {
      case 'OptionList':
        return 'dynamicIonSelect'+fieldId;
      case 'Date':
        return 'dateDaynamicField'+fieldId;
      case 'Number':
        return 'numberDaynamicField'+fieldId;
      case 'Text':
        return 'textDaynamicField'+fieldId;
      case 'Email':
        return 'emailDaynamicField'+fieldId;
      case 'phoneNumber':
        return 'phoneNumberDaynamicField'+fieldId;
      case 'searchableSelect':
        return 'searchSelectDaynamicField'+fieldId;
      case 'loqate':
          return 'loqate'+fieldId;
      default:
        return 'defaultItemID'+fieldId;
    }


  }

  getErrorIcon(fieldType) {
    switch (fieldType) {
      case 'Date':
        return 'custom-icon-error';
      default:
        return '';
    }
  }

  subscribeExtraField(){
    const parentId = this.field.fieldModel && this.field.fieldModel.Parent;
    const childId = this.field.fieldModel && this.field.fieldModel.FieldId;
    const parentControl = parentId ? this.form.controls[parentId] : null;

    if (parentControl) {
      if (this.extraFieldSubscription) {
        this.extraFieldSubscription.unsubscribe();
      }

      const parentValue = parentControl.value;
      const showOnValues = this.field.fieldModel.ShowOnParentValues || [];
      const showOccupationOther = parentValue && showOnValues.includes(parentValue.toString());

      this.hidden = !showOccupationOther;

      if (childId && this.form.controls[childId]) {
        if (showOccupationOther) {
          this.form.controls[childId].enable();
        } else {
          this.form.controls[childId].disable();
          this.form.controls[childId].reset();
        }
      }

      this.extraFieldSubscription = parentControl.valueChanges.subscribe(value => {
        const canShow = value && showOnValues.includes(value.toString());
        if (canShow) {
          this.hidden = false;
          if (childId && this.form.controls[childId])
            this.form.controls[childId].enable();
        } else {
          this.hidden = true;
          if (childId && this.form.controls[childId]) {
            this.form.controls[childId].disable();
            this.form.controls[childId].reset();
          }
        }
      });
    }
  }

  ngOnDestroy() {
    if (this.extraFieldSubscription) {
      this.extraFieldSubscription.unsubscribe();
    }
  }

  loadDynamicFieldsIdMap(){
    this.fieldsIdMap.clear();
    this.fieldsIdMap.set("8601","RegFirstName");
    this.fieldsIdMap.set("8701","RegLastName");
    this.fieldsIdMap.set("18001","RegEmail");
    this.fieldsIdMap.set("15201","RegDOB");
    this.fieldsIdMap.set("15301","RegOccupation");
    this.fieldsIdMap.set("4901","RegCountry");
    this.fieldsIdMap.set("4501","RegStreet");
    this.fieldsIdMap.set("4701","RegState");
    this.fieldsIdMap.set("4801","RegZipCode");
  }

  getStyleClass() {
    let styleClass = "item-class";
    styleClass = styleClass + ' ' + (this.newDesign ? 'field--new_design' : '');
    styleClass = styleClass + ' ' + (this.form.controls[this.field.id].invalid ? 'ng-invalid': '');
    return styleClass;
  }

  checkIfCountry(field) {
    return field.type == 'Country' || field.id == '16601' || field.id == '19001' || field.id == '17201' || field.id == 'countryOfBirth' || field.id == 'nationality';
  }

  checkIfSearchSelect(field) {
    return field.type == 'searchableSelect' && field.id != '16601' && field.id == '19001' && field.id != '17201' && field.id != 'countryOfBirth' && field.id != 'nationality';
  }


  accessFields(ev) {
    ev.forEach(value => {
      let id = value.id;
      if(!this.form.controls[id]){
        if (id === '4701') {
          id = 'state';
        } else if (id === '4501') {
          id = 'address';
        } else if (id === '4801') {
          id = 'postalCode';
        } else if (id === '4601') {
          id = 'city';
        }
      }

      if(this.form.controls[id]){
        this.form.controls[id].setValue(value.value);
        this.form.controls[id].markAsTouched();
        if(value.id  !== "4701"){
          this.ionChange.emit(value);// triggering change emit in case of loqate address change to pass the values to the upper levels.
        }
      }
    });
  }

  isValidateLithuaniaPersonalNumber(value){
    if(this.field.id === "46897" && value && value.length === 11){
      if(this.input){
        this.input.showForcedError = false;
      }
      const calculatedCheckSum = this.utilsProvider.lithuaniaPersonalNumberCheckSum(value.substring(0,10));
      const checkSum = value.substring(10,11);
      if(calculatedCheckSum === parseInt(checkSum)) {
        return true;
      }
    }
    if(value && value.lengh !== 0 && this.input){
      this.showErrotforLithuaniaPN();
    }
    return false;
  }

  getLithuaniaPersonalChecksumValidator: ValidatorFn = (control: FormControl) => {
    let validator = null;
    const valid = this.isValidateLithuaniaPersonalNumber(control.value);
    if(!valid){
      validator = {inValidPN: true};
    }
    return validator;
  }

  onClearValues(){
    this.showErrotforLithuaniaPN();
  }

  showErrotforLithuaniaPN() {
    if(this.isLithuaniaPN() === true){
      this.input.showForcedError = true;
      this.input.isErrorHidden = false;
      this.form.controls[this.field.id].markAsTouched();
      this.form.controls[this.field.id].markAsDirty();
    }
  }

  isLithuaniaPN() {
    if(this.field.id === "46897"){
      return true;
    }
    return false;
  }

  focusInput() {
    if (this.clearOnFocus && !this.focused) {
      const control = this.form.controls[this.field.id];
      // A prefilled sanitized field had its validators parked in ngOnInit; restore them before clearing,
      // otherwise the emptied field passes as valid and the form submits without an account number.
      if (this.bankNumberValidator)
        control.setValidators(this.bankNumberValidator);
      control.setValue('');
    }
    this.focused = true;
  }

  private updateErrorMsgForChileanNationalID(field) {
    const { STAGED_TRX_ID_DOCUMENT_NUMBER, ID_DOCUMENT_NUMBER } = ADDITIONAL_DETAILS_FIELDS_ID;

    if (field.id !== STAGED_TRX_ID_DOCUMENT_NUMBER && field.id !== ID_DOCUMENT_NUMBER)
      return;

    const rutMessage = this.translate.instant('page.sendMoneyAdditionalDetailsPage.ChileRUT.invalidNumber');
    const idNumberControl = this.form.controls[field.id];

    // The page adds the RUT validator only for RUT ID types, so the invalidRut error alone decides the message.
    if (idNumberControl && idNumberControl.value && idNumberControl.errors && idNumberControl.errors['invalidRut'])
      this.customMessage = rutMessage;
    else if (this.customMessage === rutMessage)
      this.customMessage = null;
  }
}



// WEBPACK FOOTER //
// ./src/components/rmt-dynamic-form-field/rmt-dynamic-form-field.ts